import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Instagram, Send, Clock, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_IMAGE = "https://images.unsplash.com/photo-1628188833435-9352c0f6fea8?crop=entropy&cs=srgb&fm=jpg&q=85";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("Mensagem enviada com sucesso!", {
        description: "Entraremos em contacto consigo brevemente."
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: ""
      });
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("Erro ao enviar mensagem", {
        description: "Por favor, tente novamente mais tarde."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] flex items-center pt-20">
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMAGE}
            alt="Produtos frescos"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#3A4A38]/90 via-[#3A4A38]/70 to-[#3A4A38]/50" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl text-white"
          >
            <span className="font-accent text-2xl text-[#D4C4A8]">
              Fala Connosco
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight mt-2 mb-6">
              Contacto
            </h1>
            <p className="text-lg text-white/80">
              Tem alguma questão ou pretende encomendar o seu cabaz?
              Estamos aqui para ajudar.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-accent text-2xl text-[#1B93A4]">
                Informações
              </span>
              <h2 className="text-3xl lg:text-4xl font-semibold text-[#2C2C2C] mt-2 mb-6">
                Como nos encontrar
              </h2>
              <div className="gold-line mb-8" />

              <p className="text-[#2C2C2C]/80 leading-relaxed mb-8">
                Estamos disponíveis para responder às suas questões, receber
                encomendas ou simplesmente conversar sobre agricultura e
                produtos frescos. Entre em contacto connosco!
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F3F1EB] rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-[#3A4A38]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2C2C2C] mb-1">Email</h4>
                    <a
                      href="mailto:QuintadaOliveirinha23@gmail.com"
                      data-testid="contact-email-link"
                      className="text-[#1B93A4] hover:underline text-sm"
                    >
                      QuintadaOliveirinha23@gmail.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F3F1EB] rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-5 h-5 text-[#3A4A38]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2C2C2C] mb-1">Telefone</h4>
                    <a
                      href="tel:+351938173638"
                      data-testid="contact-phone-link"
                      className="text-[#1B93A4] hover:underline text-sm"
                    >
                      +351 938 173 638
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F3F1EB] rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-[#3A4A38]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2C2C2C] mb-1">Morada</h4>
                    <p className="text-[#2C2C2C]/70 text-sm">
                      Gomide, Vila Verde<br />
                      Braga, Portugal
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#F3F1EB] rounded-full flex items-center justify-center flex-shrink-0">
                    <Instagram className="w-5 h-5 text-[#3A4A38]" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-[#2C2C2C] mb-1">Instagram</h4>
                    <a
                      href="https://www.instagram.com/quinta_da_oliveirinha/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="contact-instagram-link"
                      className="text-[#1B93A4] hover:underline text-sm"
                    >
                      @quinta_da_oliveirinha
                    </a>
                  </div>
                </div>
              </div>

              {/* Delivery Zone Card */}
              <Card className="mt-8 bg-[#F3F1EB] border-none">
                <CardContent className="p-6">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-[#1B93A4] mt-0.5 flex-shrink-0" />
                    <div>
                      <h4 className="font-semibold text-[#2C2C2C] mb-1">
                        Zona de Entrega
                      </h4>
                      <p className="text-sm text-[#2C2C2C]/70">
                        Entregamos todas as terças-feiras à tarde na região de
                        Braga e Vila Verde. Confirme a disponibilidade por mensagem.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <Card className="bg-[#F3F1EB] border-none">
                <CardContent className="p-6 lg:p-8">
                  {isSubmitted ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="text-center py-12"
                    >
                      <div className="w-16 h-16 bg-[#3A4A38] rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="text-2xl font-semibold text-[#2C2C2C] mb-2">
                        Mensagem Enviada!
                      </h3>
                      <p className="text-[#2C2C2C]/70 mb-6">
                        Obrigado pelo seu contacto. Responderemos o mais brevemente possível.
                      </p>
                      <Button
                        onClick={() => setIsSubmitted(false)}
                        className="btn-secondary"
                        data-testid="send-another-btn"
                      >
                        Enviar outra mensagem
                      </Button>
                    </motion.div>
                  ) : (
                    <>
                      <h3 className="text-2xl font-semibold text-[#2C2C2C] mb-2">
                        Envie-nos uma mensagem
                      </h3>
                      <p className="text-[#2C2C2C]/70 mb-6 text-sm">
                        Preencha o formulário abaixo e entraremos em contacto consigo.
                      </p>

                      <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="name" className="text-[#2C2C2C]">
                              Nome *
                            </Label>
                            <Input
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              placeholder="O seu nome"
                              required
                              className="input-field"
                              data-testid="contact-name-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email" className="text-[#2C2C2C]">
                              Email *
                            </Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="O seu email"
                              required
                              className="input-field"
                              data-testid="contact-email-input"
                            />
                          </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="phone" className="text-[#2C2C2C]">
                              Telefone
                            </Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleChange}
                              placeholder="+351 XXX XXX XXX"
                              className="input-field"
                              data-testid="contact-phone-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="subject" className="text-[#2C2C2C]">
                              Assunto *
                            </Label>
                            <Input
                              id="subject"
                              name="subject"
                              value={formData.subject}
                              onChange={handleChange}
                              placeholder="Ex: Encomenda de cabaz"
                              required
                              className="input-field"
                              data-testid="contact-subject-input"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="message" className="text-[#2C2C2C]">
                            Mensagem *
                          </Label>
                          <Textarea
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleChange}
                            placeholder="Escreva a sua mensagem aqui..."
                            required
                            rows={5}
                            className="textarea-field"
                            data-testid="contact-message-input"
                          />
                        </div>

                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="btn-primary w-full"
                          data-testid="contact-submit-btn"
                        >
                          {isSubmitting ? (
                            <>
                              <span className="animate-spin mr-2">⏳</span>
                              A enviar...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Enviar Mensagem
                            </>
                          )}
                        </Button>
                      </form>
                    </>
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section Placeholder */}
      <section className="py-20 lg:py-28 bg-[#F3F1EB]">
        <div className="container-custom">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="text-center"
          >
            <span className="font-accent text-2xl text-[#1B93A4]">
              Visite-nos
            </span>
            <h2 className="text-3xl lg:text-4xl font-semibold text-[#2C2C2C] mt-2 mb-6">
              Onde Estamos
            </h2>
            <p className="text-[#2C2C2C]/70 max-w-xl mx-auto mb-8">
              A Quinta da Oliveirinha está localizada em Gomide, Vila Verde,
              no distrito de Braga. Se pretender conhecer o nosso espaço,
              entre em contacto para agendar uma visita.
            </p>

            <div className="bg-white rounded-xl p-8 lg:p-12 border border-[#D4C4A8]/50 max-w-2xl mx-auto">
              <MapPin className="w-12 h-12 text-[#3A4A38] mx-auto mb-4" />
              <p className="text-lg font-semibold text-[#2C2C2C] mb-2">
                Gomide, Vila Verde
              </p>
              <p className="text-[#2C2C2C]/70">
                Braga, Portugal
              </p>
              <p className="text-sm text-[#1B93A4] mt-4">
                Contacte-nos para agendar uma visita à quinta
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
